/**
 * Regras da tela da carteira (layout de 10/2026): situações do calendário, cores,
 * período de um ou mais anos e os desenhos dos meses. Sem React, para ter teste.
 */
import type {
  CodigoRegime,
  Etapa,
  EstadoEtapa,
  IMesDaEmpresa,
  Parecer,
  Situacao,
  SubSituacao,
} from '../../../../hooks/useFechamentoContabilMensal';

export const NOMES_MES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];
export const ABREV_MES = NOMES_MES.map(n => n.slice(0, 3));

export interface ISituacaoDaTela {
  chave: Situacao;
  nome: string;
  curto: string;
  cor: string;
  suave: string;
  texto: string;
  subs: { chave: SubSituacao; rotulo: string; dica?: string; semDado?: boolean }[];
}

export const SITUACOES: ISituacaoDaTela[] = [
  {
    chave: 'fech',
    nome: 'Fechadas',
    curto: 'Fechada',
    cor: '#16A34A',
    suave: '#E5F5ED',
    texto: '#15803D',
    subs: [
      { chave: 'sistema', rotulo: 'Sistema' },
      {
        chave: 'area',
        rotulo: 'Área',
        dica: 'Fechada pela área, inclusive a que foi encerrada no Domínio',
      },
      {
        chave: 'sem_movimento',
        rotulo: 'Sem movimento',
        dica: 'Fechada sem movimento: pela regra, o sistema fecha sozinho',
      },
    ],
  },
  {
    chave: 'lib',
    nome: 'Liberadas p/ fechamento',
    curto: 'Liberada',
    // laranja (04/11/2026): o azul se confundia com o azul do tema
    cor: '#EA580C',
    suave: '#FFEDD5',
    texto: '#C2410C',
    subs: [
      {
        chave: 'liberar',
        rotulo: 'Liberar',
        dica: 'Sem pendência; inclui a sem movimento que espera o mês anterior fechar',
      },
      { chave: 'liberar_com_ressalvas', rotulo: 'Liberar com ressalvas' },
    ],
  },
  {
    chave: 'trav',
    nome: 'Travadas',
    curto: 'Travada',
    cor: '#DC2626',
    suave: '#FBE9E7',
    texto: '#B91C1C',
    subs: [
      { chave: 'criticos', rotulo: 'Críticos' },
      { chave: 'altos', rotulo: 'Altos' },
    ],
  },
  {
    // 04/11/2026: cliente no mês, com contabilidade, sem balancete no Domínio. Conta no
    // total, como no Acessórias; não há o que revisar até o balancete chegar.
    chave: 'sembal',
    nome: 'Sem balancete',
    curto: 'Sem balancete',
    cor: '#64748B',
    suave: '#EEF2F6',
    texto: '#475569',
    subs: [
      {
        chave: 'sem_balancete_no_ano',
        rotulo: 'Nenhum no ano',
        dica: 'O Domínio não tem balancete da empresa em nenhum mês do ano',
      },
      {
        chave: 'sem_balancete_no_mes',
        rotulo: 'Só no mês',
        dica: 'O Domínio tem balancete em outro mês do ano, mas não neste',
      },
    ],
  },
];

export const situacaoDaTela = (chave: Situacao): ISituacaoDaTela =>
  SITUACOES.find(s => s.chave === chave) ?? SITUACOES[SITUACOES.length - 1];

/** Pareceres do painel, na ordem e nas cores do layout. */
export const PARECERES_DO_PAINEL: { chave: Parecer; nome: string; cor: string; suave: string }[] = [
  { chave: 'bloquear', nome: 'Bloquear', cor: '#DC2626', suave: '#FBE9E7' },
  { chave: 'em_aberto', nome: 'Em aberto', cor: '#1B88F5', suave: '#E9F4FD' },
  {
    chave: 'liberar_com_ressalvas',
    nome: 'Liberar com ressalvas',
    cor: '#D97706',
    suave: '#FCF1DD',
  },
  { chave: 'liberar', nome: 'Liberar', cor: '#16A34A', suave: '#E5F5ED' },
  { chave: 'sem_movimento', nome: 'Sem movimento', cor: '#64748B', suave: '#EEF2F6' },
];

/** Esteira "Onde as empresas pararam". */
export const ESTEIRA: { chave: Etapa | 'concluidas'; passo: string; nome: string; cor: string }[] =
  [
    { chave: 'fiscal', passo: 'etapa 1', nome: 'Integração fiscal', cor: '#DC2626' },
    { chave: 'folha', passo: 'etapa 2', nome: 'Folha', cor: '#D97706' },
    { chave: 'banco', passo: 'etapa 3', nome: 'Conciliação', cor: '#1B88F5' },
    { chave: 'consolidacao', passo: 'etapa 4', nome: 'Consolidação', cor: '#6D28D9' },
    { chave: 'concluidas', passo: 'fim', nome: 'Concluídas', cor: '#16A34A' },
  ];

/** Barrinhas das etapas no card: ok verde, parada na cor da situação, sem dado cinza. */
export const corDaBarra = (estado: EstadoEtapa | undefined, situacao: Situacao): string => {
  if (estado === 'ok') return '#16A34A';
  if (estado === 'pendente' || estado === 'parcial')
    return situacao === 'lib' ? '#D97706' : '#DC2626';
  return '#94A3B8';
};

export const ETAPAS_DO_CARD: { chave: Etapa; nome: string }[] = [
  { chave: 'fiscal', nome: 'fiscal' },
  { chave: 'folha', nome: 'folha' },
  { chave: 'banco', nome: 'banco' },
  { chave: 'consolidacao', nome: 'fecham.' },
];

export const REGIMES: { codigo: CodigoRegime; nome: string }[] = [
  { codigo: 'LP', nome: 'Lucro Presumido' },
  { codigo: 'SN', nome: 'Simples Nacional' },
  { codigo: 'LR', nome: 'Lucro Real' },
  { codigo: 'OUTRO', nome: 'Outros' },
];

export const porcento = (parte: number, total: number): string =>
  `${total ? Math.round((parte / total) * 100) : 0}%`;

export const numero = (n: number): string => n.toLocaleString('pt-BR');

/** Receita do card: "R$ 9,6 mi", "R$ 640 mil" ou "sem receita". */
export const receitaCurta = (valor: number | null | undefined): string => {
  const v = valor ?? 0;
  if (!v) return 'sem receita';
  const abs = Math.abs(v);
  if (abs >= 1e6) return `R$ ${(v / 1e6).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mi`;
  if (abs >= 1e3) return `R$ ${Math.round(v / 1e3).toLocaleString('pt-BR')} mil`;
  return `R$ ${Math.round(v).toLocaleString('pt-BR')}`;
};

// ---- calendário: seleção de "mês:situação" (ex.: "2026-03:fech") ----

export const chaveDaSelecao = (competencia: string, situacao: Situacao): string =>
  `${competencia}:${situacao}`;

export const lerSelecao = (chave: string): { competencia: string; situacao: Situacao } => {
  const [competencia, situacao] = chave.split(':');
  return { competencia, situacao: situacao as Situacao };
};

/** Marca as chaves; se todas já estavam marcadas, desmarca. */
export const alternar = (atual: string[], chaves: string[]): string[] => {
  const todas = chaves.every(k => atual.includes(k));
  return todas
    ? atual.filter(k => !chaves.includes(k))
    : [...atual, ...chaves.filter(k => !atual.includes(k))];
};

/** Meses em faixas: [0,1,2,4] -> "jan–mar, mai"; mais de três faixas -> "N meses". */
export const faixaDeMeses = (meses: number[]): string => {
  const faixas: [number, number][] = [];
  [...meses]
    .sort((a, b) => a - b)
    .forEach(m => {
      const ult = faixas[faixas.length - 1];
      if (ult && m === ult[1] + 1) ult[1] = m;
      else faixas.push([m, m]);
    });
  if (faixas.length > 3) return `${meses.length} meses`;
  const ab = (m: number) => ABREV_MES[m].toLowerCase();
  return faixas.map(([a, b]) => (a === b ? ab(a) : `${ab(a)}–${ab(b)}`)).join(', ');
};

// ---- período da carteira: um bloco de meses aplicado a um ou mais anos ----

export type TipoPeriodo =
  | 'quinzenal'
  | 'mensal'
  | 'bimestral'
  | 'trimestral'
  | 'semestral'
  | 'anual'
  | 'personalizado';

export const TIPOS_PERIODO: [TipoPeriodo, string][] = [
  ['quinzenal', 'Quinzenal'],
  ['mensal', 'Mensal'],
  ['bimestral', 'Bimestral'],
  ['trimestral', 'Trimestral'],
  ['semestral', 'Semestral'],
  ['anual', 'Anual'],
  ['personalizado', 'Personalizado'],
];

export const QUINZENA_SO_EM_DEMONSTRACOES =
  'Por enquanto, a quinzena só está disponível em Demonstrações, na tela da empresa';

const TAMANHO: Record<string, number> = {
  mensal: 1,
  bimestral: 2,
  trimestral: 3,
  semestral: 6,
  anual: 12,
};

export interface IPeriodoCarteira {
  anos: number[];
  /** meses 0 a 11, dentro de cada ano */
  mIni: number;
  mFim: number;
  tipo: TipoPeriodo;
}

/**
 * Último mês com revisão no ano. O mês corrente não é revisado, porque o balancete dele
 * ainda está sendo lançado (decisão de 16/10/2026): no ano corrente vai até o anterior.
 */
export const ultimoMesDe = (ano: number, hoje: Date): number => {
  if (ano < hoje.getFullYear()) return 11;
  return ano === hoje.getFullYear() ? hoje.getMonth() - 1 : -1;
};

const aaaaMm = (ano: number, m: number) => `${ano}-${String(m + 1).padStart(2, '0')}`;

/** As competências do período (AAAA-MM), de todos os anos marcados. */
export const mesesDoPeriodo = (p: IPeriodoCarteira, hoje: Date): string[] =>
  [...p.anos]
    .sort((a, b) => a - b)
    .flatMap(ano => {
      const ult = ultimoMesDe(ano, hoje);
      const lista: string[] = [];
      for (let m = p.mIni; m <= Math.min(p.mFim, ult); m += 1) lista.push(aaaaMm(ano, m));
      return lista;
    });

/** Bloco da periodicidade que contém o mês (trimestral + maio = abril a junho). */
export const blocoDe = (tipo: TipoPeriodo, m: number, ultimo: number): [number, number] => {
  const t = TAMANHO[tipo];
  if (!t) return [m, m];
  const a0 = Math.floor(m / t) * t;
  return [a0, Math.max(a0, Math.min(a0 + t - 1, ultimo))];
};

/** Setas ao lado do botão: andam um bloco; com um ano só, podem trocar de ano. */
export const moverPeriodo = (
  p: IPeriodoCarteira,
  dir: -1 | 1,
  hoje: Date,
  anoMinimo: number,
): IPeriodoCarteira | null => {
  const t = TAMANHO[p.tipo] ?? p.mFim - p.mIni + 1;
  const a = TAMANHO[p.tipo] ? Math.floor(p.mIni / t) * t + dir * t : p.mIni + dir * t;
  const b = a + t - 1;
  const ultimoDosAnos = Math.max(...p.anos.map(y => ultimoMesDe(y, hoje)));
  if (a >= 0 && b <= 11) {
    if (a > ultimoDosAnos) return null;
    return { ...p, mIni: a, mFim: Math.min(b, Math.max(a, ultimoDosAnos)) };
  }
  if (p.anos.length > 1) return null;
  const ano = p.anos[0] + dir;
  if (ano < anoMinimo || ultimoMesDe(ano, hoje) < 0) return null;
  const a2 = a - dir * 12;
  if (a2 > ultimoMesDe(ano, hoje)) return null;
  return { ...p, anos: [ano], mIni: a2, mFim: Math.min(a2 + t - 1, ultimoMesDe(ano, hoje)) };
};

const textoDosAnos = (anos: number[]): string => {
  const a = [...anos].sort((x, y) => x - y);
  return a.length === 1 ? String(a[0]) : `${a.slice(0, -1).join(', ')} e ${a[a.length - 1]}`;
};

/** Texto do botão: "03/2026", "03/2026 a 05/2026" ou "03 a 05 · 2025 e 2026". */
export const textoDoPeriodo = (p: IPeriodoCarteira): string => {
  const mm = (m: number) => String(m + 1).padStart(2, '0');
  const meses = p.mIni === p.mFim ? mm(p.mIni) : `${mm(p.mIni)} a ${mm(p.mFim)}`;
  if (p.anos.length > 1) return `${meses} · ${textoDosAnos(p.anos)}`;
  const y = p.anos[0];
  return p.mIni === p.mFim ? `${mm(p.mIni)}/${y}` : `${mm(p.mIni)}/${y} a ${mm(p.mFim)}/${y}`;
};

/** Título da carteira: "Carteira · Setembro de 2026" ou "Carteira · mar–mai de 2025 e 2026". */
export const tituloDoPeriodo = (p: IPeriodoCarteira): string => {
  const meses =
    p.mIni === p.mFim
      ? NOMES_MES[p.mIni]
      : `${ABREV_MES[p.mIni].toLowerCase()}–${ABREV_MES[p.mFim].toLowerCase()}`;
  return `Carteira · ${meses} de ${textoDosAnos(p.anos)}`;
};

/** Faixa escrita no rodapé do seletor: "mar — mai · 2026". */
export const faixaDoRascunho = (p: IPeriodoCarteira): string => {
  const ab = (m: number) => ABREV_MES[m].toLowerCase();
  const meses = p.mIni === p.mFim ? ab(p.mIni) : `${ab(p.mIni)} — ${ab(p.mFim)}`;
  return `${meses} · ${textoDosAnos(p.anos)}`;
};

// ---- desenhos dos meses: o marco do calendário contábil que mais pesa em cada um ----

const DESENHOS = [
  // calculadora
  'M20 8h24a4 4 0 0 1 4 4v40a4 4 0 0 1-4 4H20a4 4 0 0 1-4-4V12a4 4 0 0 1 4-4zM22 14h20v10H22zM22 30h4v4h-4zM30 30h4v4h-4zM38 30h4v4h-4zM22 38h4v4h-4zM30 38h4v4h-4zM38 38h4v12h-4zM22 46h4v4h-4zM30 46h4v4h-4z',
  // livro razão aberto
  'M32 16C26 12 16 12 8 14v34c8-2 18-2 24 2 6-4 16-4 24-2V14c-8-2-18-2-24 2zM32 16v34M14 22h12M14 28h12M14 34h10M38 22h12M38 28h12M38 34h10',
  // moedas empilhadas e cifrão
  'M10 32c0-3 6-5 14-5s14 2 14 5-6 5-14 5-14-2-14-5zM10 32v6c0 3 6 5 14 5s14-2 14-5v-6M10 38v6c0 3 6 5 14 5s14-2 14-5v-6M38 22a10 10 0 1 0 20 0a10 10 0 1 0-20 0M48 15v14M51 18.5c-.8-.9-1.9-1.5-3-1.5-1.8 0-3 1-3 2.3 0 3 6 1.6 6 4.4 0 1.3-1.2 2.3-3 2.3-1.2 0-2.4-.6-3.2-1.5',
  // nota fiscal / recibo
  'M18 8h28v48l-4-3-4 3-4-3-4 3-4-3-4 3-4-3V8zM24 18h16M24 24h16M24 30h10M24 42h8M36 42h4',
  // gráfico de barras com tendência
  'M10 54h44M14 54V40h8v14M28 54V30h8v24M42 54V20h8v34M12 30l14-10 10 6 16-14M46 12h6v6',
  // pasta de arquivo com documento
  'M8 20a4 4 0 0 1 4-4h12l4 5h24a4 4 0 0 1 4 4v25a4 4 0 0 1-4 4H12a4 4 0 0 1-4-4zM8 28h48M22 21V10h22v11M27 15h12',
  // carimbo
  'M26 10h12a4 4 0 0 1 0 8h-2v10h8a6 6 0 0 1 6 6v6H14v-6a6 6 0 0 1 6-6h8V18h-2a4 4 0 0 1 0-8zM12 46h40M16 52h32',
  // lupa sobre documento
  'M12 8h24l8 8v36H12zM36 8v8h8M18 22h16M18 28h12M18 34h8M36 40a8 8 0 1 0 16 0a8 8 0 1 0-16 0M50 46l8 8',
  // pasta executiva
  'M10 22h44v28a3 3 0 0 1-3 3H13a3 3 0 0 1-3-3zM24 22v-6a3 3 0 0 1 3-3h10a3 3 0 0 1 3 3v6M10 34h44M30 32h4v5h-4z',
  // cofrinho
  'M14 34c0-10 9-16 20-16s20 6 20 14c0 4-2 8-6 10v8h-6v-5H28v5h-6v-8c-4-2-8-5-8-8zM29 14h10M44 28h.5M14 32l-6-2',
  // prancheta com checklist
  'M18 12h28a2 2 0 0 1 2 2v40a2 2 0 0 1-2 2H18a2 2 0 0 1-2-2V14a2 2 0 0 1 2-2zM26 8h12v8H26zM22 26l3 3 5-6M34 26h8M22 36l3 3 5-6M34 36h8M22 46l3 3 5-6M34 46h8',
  // monitor com planilha
  'M8 12h48v32H8zM24 52h16M32 44v8M12 20h40M12 28h40M12 36h40M24 16v24M38 16v24',
];

export const MARCOS_DO_MES: { desenho: string; titulo: string; texto: string }[] = [
  {
    d: 10,
    t: 'Prancheta · Escolha do regime',
    x: 'Opção pelo Simples Nacional até o último dia útil de janeiro. É o checklist de abertura do exercício.',
  },
  {
    d: 3,
    t: 'Recibo · Informe de rendimentos',
    x: 'Empresas entregam o informe de rendimentos a funcionários e prestadores até o último dia útil de fevereiro.',
  },
  {
    d: 11,
    t: 'Monitor · DEFIS',
    x: 'Empresas do Simples enviam a DEFIS até 31 de março. O mês também fecha o 1º trimestre e abre a temporada do Imposto de Renda.',
  },
  {
    d: 0,
    t: 'Calculadora · IRPJ e CSLL do 1º trimestre',
    x: 'No lucro presumido e no real trimestral, o imposto do 1º trimestre vence no último dia útil de abril. Dia 25 é o Dia do Contabilista.',
  },
  {
    d: 7,
    t: 'Lupa · Imposto de Renda',
    x: 'Fim do prazo da declaração do IRPF dos sócios e clientes. A lupa lembra a malha fina.',
  },
  {
    d: 1,
    t: 'Livro razão · ECD',
    x: 'A Escrituração Contábil Digital (livro Diário e Razão do ano anterior) vai ao SPED até o último dia útil de junho.',
  },
  {
    d: 5,
    t: 'Pasta · ECF',
    x: 'A Escrituração Contábil Fiscal reúne a apuração anual de IRPJ e CSLL e vence no último dia útil de julho, junto com o imposto do 2º trimestre.',
  },
  {
    d: 4,
    t: 'Gráfico · Balanço do semestre',
    x: 'Primeiro mês depois das grandes entregas: hora de revisar o resultado do 1º semestre com o cliente.',
  },
  {
    d: 6,
    t: 'Carimbo · Dia do Contador',
    x: 'Dia 22 de setembro é o Dia do Contador. O mês também fecha o 3º trimestre.',
  },
  {
    d: 9,
    t: 'Cofrinho · Planejamento tributário',
    x: 'IRPJ e CSLL do 3º trimestre no fim do mês e início do planejamento: qual regime vale mais a pena no ano que vem.',
  },
  {
    d: 2,
    t: 'Moedas · 1ª parcela do 13º',
    x: 'A primeira parcela do décimo terceiro salário deve ser paga até 30 de novembro.',
  },
  {
    d: 8,
    t: 'Pasta executiva · Encerramento do exercício',
    x: '2ª parcela do 13º até o dia 20, inventário e balanço de 31 de dezembro: o ano contábil fecha.',
  },
].map(({ d, t, x }) => ({ desenho: DESENHOS[d], titulo: t, texto: x }));

/** Situação de um mês da empresa; a mesma regra do servidor (carteira.situacao_do_mes). */
export const situacaoDoMes = (m: Pick<IMesDaEmpresa, 'fechada' | 'status'>): Situacao => {
  if (m.status === 'sem_balancete') return 'sembal';
  if (m.fechada) return 'fech';
  if (['liberar', 'liberar_com_ressalvas', 'sem_movimento'].includes(m.status)) return 'lib';
  return 'trav';
};

/** Empresa marcada nos cards para o detalhe (uma competência de cada). */
export interface IEmpresaMarcada {
  id: number;
  razao: string;
  competencia: string;
}

export const chaveDaMarcada = (e: Pick<IEmpresaMarcada, 'id' | 'competencia'>): string =>
  `${e.id}|${e.competencia}`;

/** Clique simples no card: entra na seleção ou sai dela. */
export const alternarMarcada = (
  lista: IEmpresaMarcada[],
  e: IEmpresaMarcada,
): IEmpresaMarcada[] => {
  const k = chaveDaMarcada(e);
  return lista.some(x => chaveDaMarcada(x) === k)
    ? lista.filter(x => chaveDaMarcada(x) !== k)
    : [...lista, e];
};

/** Etiquetas do carrossel: com muitas empresas, só 3 em volta da atual e um "+N". */
export const janelaDoCarrossel = (
  total: number,
  atual: number,
  expandido: boolean,
  limite = 3,
): [number, number] => {
  if (expandido || total <= limite + 1) return [0, total];
  const ini = Math.max(0, Math.min(atual - 1, total - limite));
  return [ini, ini + limite];
};

/** Parte de um valor sobre a receita líquida; sem receita, não há margem. */
export const sobreAReceita = (
  valor: number | null | undefined,
  receitaLiquida: number | null | undefined,
): number | null => (valor == null || !receitaLiquida ? null : valor / receitaLiquida);
